import { useState } from "react";
import axios from "axios";
import { useSite } from "./SiteContext";
import { AviahMark } from "./AviahMark";
import { X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Premium construction gate — shown to the public in Private Preview /
// Maintenance mode. The password is verified server-side only.
const ConstructionPage = ({ onSuccess, required }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await axios.post(
        `${API}/preview/verify`,
        { password },
        { withCredentials: true, headers: { "X-Requested-With": "XMLHttpRequest" } },
      );
      onSuccess();
    } catch (err) {
      const status = err?.response?.status;
      setError(status === 429 ? "Too many attempts. Please wait a few minutes." : "Incorrect password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#171717]" data-testid="construction-gate">
      <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 opacity-[0.06]">
        <AviahMark className="h-full w-full text-[#C6A46A]" strokeWidth={1.6} />
      </div>
      <div className="pointer-events-none absolute -bottom-32 -left-32 h-[28rem] w-[28rem] opacity-[0.05]">
        <AviahMark className="h-full w-full text-[#C6A46A]" strokeWidth={1.6} />
      </div>

      <div className="relative mx-auto max-w-lg px-6 text-center">
        <img src="/assets/logo-gold-film.webp" alt="Aviah Financial" className="mx-auto w-64 sm:w-80" />
        <div className="hairline-x mx-auto mt-10 max-w-40" />
        <h1 className="mt-10 text-xl font-light uppercase tracking-[0.28em] text-[#F4F0E8] sm:text-2xl">
          Website Under Construction
        </h1>
        <p className="mt-6 text-sm leading-relaxed text-[#F4F0E8]/60">
          We are currently preparing a new digital experience.
          <br />
          Please check back soon.
        </p>
        <p className="mt-4 text-sm leading-relaxed text-[#F4F0E8]/40">
          Estamos preparando una nueva experiencia digital.
          <br />
          Muy pronto estaremos disponibles.
        </p>

        {required && (
          <form onSubmit={submit} className="mx-auto mt-12 max-w-xs" data-testid="preview-form">
            <label htmlFor="preview-password" className="eyebrow block text-[#C6A46A]">
              Client Preview
            </label>
            <input
              id="preview-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="preview-password-input"
              autoComplete="off"
              className="mt-4 w-full rounded-[3px] border border-[#F4F0E8]/20 bg-[#1E1E1E] px-4 py-3 text-center text-sm tracking-[0.2em] text-[#F4F0E8] transition-colors focus:border-[#C6A46A] focus:outline-none"
              placeholder="••••••••••••"
            />
            {error && (
              <p className="mt-3 text-xs text-[#C6A46A]" data-testid="preview-error">{error}</p>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="preview-enter-button"
              className="mt-5 w-full rounded-[3px] border border-[#C6A46A] bg-[#C6A46A] px-6 py-3 text-[0.68rem] font-medium uppercase tracking-[0.2em] text-[#171717] transition-colors duration-300 hover:bg-transparent hover:text-[#C6A46A] disabled:opacity-60"
            >
              Enter Website
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

const PreviewBadge = ({ onExit }) => (
  <div
    className="fixed bottom-4 left-4 z-[70] flex items-center gap-3 rounded-[3px] border border-[#C6A46A]/50 bg-[#171717]/90 px-3 py-1.5 backdrop-blur-sm"
    data-testid="preview-badge"
  >
    <span className="text-[0.55rem] font-medium uppercase tracking-[0.2em] text-[#C6A46A]">Private Preview</span>
    <button
      onClick={onExit}
      className="flex items-center gap-1 text-[0.55rem] uppercase tracking-[0.15em] text-[#F4F0E8]/50 transition-colors hover:text-[#F4F0E8]"
      data-testid="preview-exit"
    >
      <X className="h-3 w-3" /> Exit
    </button>
  </div>
);

// Wraps the public site: gates it when the website is not in Public mode.
const AccessGate = ({ children }) => {
  const { status, reloadStatus, draftPreview } = useSite();

  if (!status) return <div className="min-h-screen bg-[#171717]" />;

  const gated = status.mode !== "public" && !status.previewActive;

  if (gated) {
    return <ConstructionPage onSuccess={reloadStatus} required={status.previewRequired !== false} />;
  }

  const exitPreview = async () => {
    await axios.post(`${API}/preview/logout`, {}, { withCredentials: true, headers: { "X-Requested-With": "XMLHttpRequest" } });
    reloadStatus();
  };

  return (
    <>
      {children}
      {status.mode !== "public" && status.previewActive && <PreviewBadge onExit={exitPreview} />}
      {draftPreview && (
        <div className="fixed bottom-4 right-4 z-[70] rounded-[3px] border border-[#C6A46A]/50 bg-[#171717]/90 px-3 py-1.5 backdrop-blur-sm" data-testid="draft-preview-badge">
          <span className="text-[0.55rem] font-medium uppercase tracking-[0.2em] text-[#C6A46A]">Draft Preview</span>
        </div>
      )}
    </>
  );
};

export default AccessGate;
